// @mui
import { useSettingsContext } from 'src/components/settings';
import { useState, useCallback } from 'react';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';

// components
import Iconify from 'src/components/iconify';
import { hideScroll } from 'src/theme/css';
import HeroSection from './hero-section';
import CommunitySection from './community-section';
import ProductFeatureSection from './product-feature-section';
import WorkFlowSection from './work-flow-section';
import TestomonialSection from './testomonial-section';
import ConstructionIndustrySection from './construction-industry-section';
import PricePackagesSection from './price-packages-section';
import ContactUsSection from './contact-us-section';
import BetterSolutionSection from './better-solution-section';

// ----------------------------------------------------------------------

const TABS = [
  {
    value: 'Hero Section',
    label: 'Hero Section',
    icon: <Iconify icon="solar:home-bold" width={22} />,
    color: '#4CAF50',
  },
  {
    value: 'Community',
    label: 'Community',
    icon: <Iconify icon="ant-design:solution-outlined" width={22} />,
    color: '#2196F3',
  },
  {
    value: 'Product Features',
    label: 'Product Features',
    icon: <Iconify icon="solar:box-bold" width={22} />,
    color: '#FF9800',
  },
  {
    value: 'Work Flow Process',
    label: 'Work Flow Process',
    icon: <Iconify icon="solar:document-bold" width={22} />,
    color: '#E91E63',
  },
  {
    value: 'Testimonials',
    label: 'Testimonials',
    icon: <Iconify icon="material-symbols:rate-review" width={22} />,
    color: '#9C27B0',
  },
  {
    value: 'Construction Industry',
    label: 'Construction Industry',
    icon: <Iconify icon="mdi:crane" width={22} />,
    color: '#795548',
  },
  {
    value: 'Price & Packages',
    label: 'Price & Packages',
    icon: <Iconify icon="material-symbols:package-2" width={22} />,
    color: '#607D8B',
  },
  {
    value: 'Contact Us',
    label: 'Contact Us',
    icon: <Iconify icon="material-symbols:contact-support" width={22} />,
    color: '#00BCD4',
  },
  {
    value: 'Better Solution',
    label: 'Better Solution',
    icon: <Iconify icon="carbon:idea" width={22} />,
    color: '#673AB7',
  },
];

export default function WebsiteHomePage() {
  const settings = useSettingsContext();
  const theme = useTheme();

  const [currentTab, setCurrentTab] = useState('Hero Section');

  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  return (
    <>
      {/* <Card> */}
        {/* <CardHeader
          title="Website Home Page Sections"
          subheader="Manage content for each section of your website's home page"
        />
        <Divider /> */}
        <Box
          sx={{
            position: 'relative',
            px: 3,
            pt: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 1,
              bgcolor: (theme) => alpha(theme.palette.primary.lighter, 0.8),
              mb: 2,
              py:0,
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              sx={{
                width: '100%',
                overflowX: 'auto',
                ...hideScroll.x,
                flexWrap: { xs: 'nowrap', md: 'wrap' },
                pb: { xs: 1, md: 0 },
              }}
            >
              <Tabs
                value={currentTab}
                onChange={handleChangeTab}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                TabIndicatorProps={{
                  sx: {
                    display: 'none', // Remove indicator completely
                  },
                }}
                sx={{
                  px: 2,
                  '& .MuiTabs-flexContainer': {
                    gap: 0,
                  },
                  '& .MuiTab-root': {
                    minWidth: 'auto',
                    minHeight: 40,
                    fontWeight: 600,
                    borderRadius: 1,
                    mt: 0.5,
                    textTransform: 'capitalize',
                    px: 1,
                    '&:hover': {
                      bgcolor: 'background.paper',
                      color: 'primary.main',
                    },
                    '&.Mui-selected': {
                      bgcolor: 'background.paper',
                      color: 'primary.main',
                      boxShadow: theme.customShadows.z8,
                    },
                  },
                }}
              >
                {TABS.map((tab) => (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {tab.icon}
                        </Box>
                        <Box component="span">{tab.label}</Box>
                      </Stack>
                    }
                    sx={{
                      '&.Mui-selected': {
                        color: tab.color,
                        '& svg': { color: tab.color },
                      },
                    }}
                  />
                ))}
              </Tabs>
            </Stack>
          </Paper>
        </Box>

        <Box sx={{ p: 3 }}>
          {currentTab === 'Hero Section' && (
            <Box sx={{ mt: 1 }}>
              <HeroSection />
            </Box>
          )}

          {currentTab === 'Community' && (
            <Box sx={{ p: 3, bgcolor: (theme) => alpha(theme.palette.primary.lighter, 0.08), borderRadius: 2 }}>
              <Stack spacing={2}>
              <CommunitySection/>
              </Stack>
            </Box>
          )}

          {currentTab === 'Product Features' && (
            <Box sx={{ p: 3, bgcolor: (theme) => alpha(theme.palette.primary.lighter, 0.08), borderRadius: 2 }}>
              <Stack spacing={2}>
              <ProductFeatureSection />
              </Stack>
            </Box>
          )}

          {currentTab === 'Work Flow Process' && (
            <Box sx={{ p: 3, bgcolor: (theme) => alpha(theme.palette.primary.lighter, 0.08), borderRadius: 2 }}>
              <Stack spacing={2}>
                <WorkFlowSection/>
              </Stack>
            </Box>
          )}

          {currentTab === 'Testimonials' && (
            <Box sx={{ p: 3, bgcolor: (theme) => alpha(theme.palette.primary.lighter, 0.08), borderRadius: 2 }}>
              <Stack spacing={2}>
               <TestomonialSection/>
              </Stack>
            </Box>
          )}

          {currentTab === 'Construction Industry' && (
            <Box sx={{ p: 3, bgcolor: (theme) => alpha(theme.palette.primary.lighter, 0.08), borderRadius: 2 }}>
              <Stack spacing={2}>
              <ConstructionIndustrySection />
              </Stack>
            </Box>
          )}

          {currentTab === 'Price & Packages' && (
            <Box sx={{ p: 3, bgcolor: (theme) => alpha(theme.palette.primary.lighter, 0.08), borderRadius: 2 }}>
              <Stack spacing={2}>
               <PricePackagesSection />
              </Stack>
            </Box>
          )}

          {currentTab === 'Contact Us' && (
            <Box sx={{ p: 3, bgcolor: (theme) => alpha(theme.palette.primary.lighter, 0.08), borderRadius: 2 }}>
              <Stack spacing={2}>
                <ContactUsSection/>
              </Stack>
            </Box>
          )}

          {currentTab === 'Better Solution' && (
            <Box sx={{ p: 3, bgcolor: (theme) => alpha(theme.palette.primary.lighter, 0.08), borderRadius: 2 }}>
              <Stack spacing={2}>
                <BetterSolutionSection />
              </Stack>
            </Box>
          )}
        </Box>
      {/* </Card> */}
    </>
  );
}
